import React, { useState, useRef } from 'react';
import { Modal, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { FaEnvelope, FaUser, FaPhoneAlt , FaComment, FaCheckCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import API from '../../services/api';

const ContactModal = ({ show, onHide, courseName, courseId }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const timeoutRef = useRef(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.phone.trim()) newErrors.phone = 'Phone number is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', phone: '', message: '' });
    setErrors({});
  };

  const handleClose = () => {
    // Clear any pending timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    resetForm();
    setSubmitted(false);
    onHide();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      const subject = courseName ? `Enrollment Request for ${courseName}` : 'General Support Query';
      const message = formData.message || (courseName ? `I would like to enroll in the "${courseName}" course. Please contact me with further details.` : 'I need assistance. Please contact me.');
      
      const response = await API.post('/contact-messages', {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: subject,
        message: message
      });
      
      // FIXED: Check for both 'status' and 'success' properties
      if (response.data.status === true || response.data.success === true) {
        setSubmitted(true);
        toast.success('Request submitted successfully! Our team will contact you soon.');
        
        // Reset form data after successful submission
        resetForm();
        
        // Clear any existing timeout
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        
        // Close modal after 3 seconds
        timeoutRef.current = setTimeout(() => {
          setSubmitted(false);
          onHide();
          timeoutRef.current = null;
        }, 3000);
      } else {
        toast.error(response.data.message || 'Failed to submit request');
      }
    } catch (error) {
      console.error('Submission error:', error);
      const errorMessage = error.response?.data?.message || 'Failed to submit request. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="md">
      <Modal.Header closeButton className="bg-primary text-white">
        <Modal.Title>
          <FaEnvelope className="me-2" />
          {courseName ? 'Request Enrollment' : 'Contact Support'}
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {submitted ? (
          <div className="text-center py-4">
            <FaCheckCircle size={60} className="text-success mb-3" />
            <h5 className="text-success">Request Submitted Successfully!</h5>
            {courseName && (
              <p className="text-muted mt-3">
                Thank you for your interest in <strong>{courseName}</strong>.
              </p>
            )}
            <p className="text-muted">
              Our team will contact you within 24 hours to assist you.
            </p>
            <div className="mt-3 p-3 bg-light rounded">
              <small className="text-muted">
                📞 For urgent queries, call us at <strong>+91 77228-99444</strong>
              </small>
            </div>
            <Button 
              variant="outline-primary" 
              size="sm" 
              className="mt-3"
              onClick={handleClose}
            >
              Close
            </Button>
          </div>
        ) : (
          <>
            <Alert variant="info" className="mb-3">
              <small>
                ℹ️ {courseName ? 'Please fill the form below and our team will contact you within 24 hours.' : 'Fill the form below and our support team will get back to you.'}
              </small>
            </Alert>
            
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Full Name *</Form.Label>
                <div className="input-icon-wrapper">
                  <FaUser className="input-icon" />
                  <Form.Control
                    type="text"
                    name="name"
                    placeholder="Enter your full name"
                    value={formData.name}
                    onChange={handleChange}
                    isInvalid={!!errors.name}
                    className="ps-4"
                  />
                </div>
                <Form.Control.Feedback type="invalid">
                  {errors.name}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Email Address *</Form.Label>
                <div className="input-icon-wrapper">
                  <FaEnvelope className="input-icon" />
                  <Form.Control
                    type="email"
                    name="email"
                    placeholder="Enter your email address"
                    value={formData.email}
                    onChange={handleChange}
                    isInvalid={!!errors.email}
                    className="ps-4"
                  />
                </div>
                <Form.Control.Feedback type="invalid">
                  {errors.email}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Phone Number *</Form.Label>
                <div className="input-icon-wrapper">
                  <FaPhoneAlt  className="input-icon" />
                  <Form.Control
                    type="tel"
                    name="phone"
                    placeholder="Enter your phone number"
                    value={formData.phone}
                    onChange={handleChange}
                    isInvalid={!!errors.phone}
                    className="ps-4"
                  />
                </div>
                <Form.Control.Feedback type="invalid">
                  {errors.phone}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Message (Optional)</Form.Label>
                <div className="input-icon-wrapper">
                  <FaComment className="input-icon" />
                  <Form.Control
                    as="textarea"
                    name="message"
                    rows={3}
                    placeholder="Any specific questions or requirements?"
                    value={formData.message}
                    onChange={handleChange}
                    className="ps-4"
                  />
                </div>
              </Form.Group>

              {courseName && (
                <div className="mb-3 p-2 bg-light rounded">
                  <small className="text-muted">
                    <strong>Course Selected:</strong> {courseName}
                  </small>
                </div>
              )}

              <div className="d-flex gap-2">
                <Button variant="secondary" onClick={handleClose} className="flex-grow-1">
                  Cancel
                </Button>
                <Button type="submit" variant="primary" disabled={loading} className="flex-grow-1">
                  {loading ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Request'
                  )}
                </Button>
              </div>
            </Form>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ContactModal;